"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import type { AppointmentDraft } from "@/components/appointments/appointment-editor";
import { AppointmentSheet } from "@/components/appointments/sheet/appointment-sheet";

/**
 * Appointments rebuild 86 (D4): ONE appointment sheet for the whole desktop
 * app, mounted once in the dashboard layout. Every surface that used to own
 * its own show-modal / form-dialog state (calendar chips, list rows, lead
 * tabs, dashboard widgets, waiting room) now calls `openAppointment(id)` or
 * `createAppointment(draft)` and hands over a refresh callback.
 */
export interface SheetClosedInfo {
  /** True when at least one save / status / check-in / delete happened. */
  changed: boolean;
}

export interface OpenAppointmentOptions {
  /** Refresh hook for the surface that opened the sheet. */
  onChanged?: () => void;
  /** Fired once when the sheet closes (the phone's booking flow navigates on it). */
  onClosed?: (info: SheetClosedInfo) => void;
  /** Open straight into the editable fields. */
  edit?: boolean;
}

export interface CreateAppointmentOptions {
  onChanged?: () => void;
  onClosed?: (info: SheetClosedInfo) => void;
}

export interface AppointmentSheetApi {
  openAppointment: (appointmentId: string, options?: OpenAppointmentOptions) => void;
  createAppointment: (draft?: AppointmentDraft, options?: CreateAppointmentOptions) => void;
  close: () => void;
}

interface SheetRequest {
  /** Bumps on every open so the sheet remounts its body (fresh fetch, clean form). */
  key: number;
  appointmentId: string | null;
  draft: AppointmentDraft | null;
  edit: boolean;
  onChanged?: () => void;
  onClosed?: (info: SheetClosedInfo) => void;
}

const AppointmentSheetContext = createContext<AppointmentSheetApi | null>(null);

export function AppointmentSheetProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<SheetRequest | null>(null);

  const openAppointment = useCallback((appointmentId: string, options?: OpenAppointmentOptions) => {
    setRequest((prev) => ({
      key: (prev?.key ?? 0) + 1,
      appointmentId,
      draft: null,
      edit: options?.edit ?? false,
      onChanged: options?.onChanged,
      onClosed: options?.onClosed,
    }));
    setOpen(true);
  }, []);

  const createAppointment = useCallback((draft?: AppointmentDraft, options?: CreateAppointmentOptions) => {
    setRequest((prev) => ({
      key: (prev?.key ?? 0) + 1,
      appointmentId: null,
      draft: draft ?? {},
      edit: true,
      onChanged: options?.onChanged,
      onClosed: options?.onClosed,
    }));
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const api = useMemo<AppointmentSheetApi>(
    () => ({ openAppointment, createAppointment, close }),
    [openAppointment, createAppointment, close]
  );

  return (
    <AppointmentSheetContext.Provider value={api}>
      {children}
      {request && (
        <AppointmentSheet
          key={request.key}
          open={open}
          onOpenChange={setOpen}
          appointmentId={request.appointmentId}
          draft={request.draft}
          initialEdit={request.edit}
          onChanged={request.onChanged}
          onClosed={request.onClosed}
        />
      )}
    </AppointmentSheetContext.Provider>
  );
}

export function useAppointmentSheet(): AppointmentSheetApi {
  const ctx = useContext(AppointmentSheetContext);
  if (!ctx) {
    throw new Error("useAppointmentSheet must be used inside <AppointmentSheetProvider>");
  }
  return ctx;
}
